import { useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { useStepScreenshot } from "@/hooks/use-step-screenshot";
import { cn } from "@/lib/utils";
import {
  type HighlightTone,
  type ScreenshotHighlight,
  type TargetBox,
  isValidBox,
} from "./screenshot-types";
import { ScreenshotSvg } from "./screenshot-svg";

/**
 * Lightweight, non-interactive thumbnail viewer.
 * Guaranteed safe to embed inside `<Link>` or `<button>` without nested interactive controls.
 */
export function StepThumbnail({
  walkthroughId,
  stepNumber,
  alt,
  className,
  targetBox,
  targetTone,
  highlights,
}: {
  walkthroughId: string | null | undefined;
  stepNumber: number | null | undefined;
  alt: string;
  className?: string;
  targetBox?: TargetBox | null;
  targetTone?: HighlightTone | string | null;
  highlights?: ScreenshotHighlight[];
}) {
  const { url, loading } = useStepScreenshot(walkthroughId, stepNumber);

  const activeHighlights = useMemo(() => {
    if (highlights && highlights.length > 0) return highlights;
    if (isValidBox(targetBox)) {
      return [{ id: "target", box: targetBox, tone: targetTone }];
    }
    return [];
  }, [highlights, targetBox, targetTone]);

  if (loading) {
    return <Skeleton className={cn("aspect-video w-full", className)} />;
  }

  if (!url) {
    return (
      <div
        className={cn(
          "flex aspect-video w-full items-center justify-center bg-muted/30 text-xs text-muted-foreground",
          className,
        )}
      >
        No screenshot
      </div>
    );
  }

  return (
    <div
      className={cn(
        "relative flex h-full w-full items-center justify-center overflow-hidden",
        className,
      )}
    >
      <ScreenshotSvg
        url={url}
        alt={alt}
        highlights={activeHighlights}
        className="h-full w-full object-contain"
      />
    </div>
  );
}
